import { StyleSheet, css } from 'aphrodite';
import * as React from 'react';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { lightGray, shuffleGray, aceGray, charmGray, titles, darkGrayFontColor } from '../../../web/styles/colors';

const { colors, spacing } = THEME;

// Theme color mappings using the centralized color system
const THEME_COLORS = {
	grey: {
		100: lightGray,
		200: shuffleGray,
		300: aceGray,
		400: charmGray,
	},
	text: {
		primary: titles,
		secondary: darkGrayFontColor,
		disabled: charmGray,
	},
};

// Base contact type that both FlowContact and TestContact can extend
// Made compatible with Api.IContact which has all optional properties
export interface BaseContact {
	id?: string;
	firstName?: string;
	lastName?: string;
	profilePic?: string;
	tags?: string[];
	keyFacts?: string[];
	keyFactsCollection?: { value: string }[];
	primaryEmail?: { value: string };
	phoneNumbers?: { value: string }[];
}

interface ContactCardProps<T extends BaseContact> {
	contact: T;
	onToggle: (contactId: string) => void;
	isActive?: boolean;
	isSelected?: boolean;
	disabled?: boolean;
	color?: string;
	// Optional render prop for additional content (like progress indicators)
	renderAdditionalContent?: (contact: T) => React.ReactNode;
	// Options for customizing display
	showEmail?: boolean;
	showPhone?: boolean;
	showAllTags?: boolean;
	showAllKeyFacts?: boolean;
	filterKeyFacts?: (fact: string) => boolean;
}

/**
 * Unified ContactCard component for automation flows.
 * Supports both web and admin use cases with flexible props.
 */
export function ContactCard<T extends BaseContact>({
	contact,
	onToggle,
	isActive,
	isSelected,
	disabled = false,
	color = colors.primary.main,
	renderAdditionalContent,
	showEmail = false,
	showPhone = false,
	showAllTags = false,
	showAllKeyFacts = false,
	filterKeyFacts,
}: ContactCardProps<T>) {
	const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
	const checked = isSelected ?? isActive ?? false;

	// Handle key facts from either format
	const keyFactsList = React.useMemo(() => {
		if (contact.keyFacts) {
			return filterKeyFacts ? contact.keyFacts.filter(filterKeyFacts) : contact.keyFacts;
		}
		if (contact.keyFactsCollection) {
			const facts = contact.keyFactsCollection.map(kf => kf.value);
			return filterKeyFacts ? facts.filter(filterKeyFacts) : facts;
		}
		return [];
	}, [contact.keyFacts, contact.keyFactsCollection, filterKeyFacts]);

	const handleClick = () => {
		if (!disabled && contact.id) {
			onToggle(contact.id);
		}
	};

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.stopPropagation();
		if (!disabled && contact.id) {
			onToggle(contact.id);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if ((e.key === 'Enter' || e.key === ' ') && !disabled && contact.id) {
			e.preventDefault();
			onToggle(contact.id);
		}
	};

	return (
		<div
			className={css(styleSheet.card, disabled && styleSheet.disabled, checked && styleSheet.selected)}
			style={{ borderColor: color }}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			role='button'
			tabIndex={disabled ? -1 : 0}
			aria-pressed={checked}
			aria-label={`${checked ? 'Deselect' : 'Select'} ${fullName}`}
		>
			<div className={css(styleSheet.colorIndicator)} style={{ backgroundColor: color }} />

			<div className={css(styleSheet.header)}>
				<div className={css(styleSheet.nameSection)}>
					<div className={css(styleSheet.profilePic)}>
						{contact.profilePic ? (
							<img src={contact.profilePic} alt={fullName} className={css(styleSheet.profileImage)} />
						) : (
							<span className={css(styleSheet.initials)}>
								{contact.firstName?.[0] || ''}
								{contact.lastName?.[0] || ''}
							</span>
						)}
					</div>
					<span className={css(styleSheet.name)}>{fullName}</span>
				</div>
				<input
					type='checkbox'
					checked={checked}
					onChange={handleCheckboxChange}
					disabled={disabled}
					className={css(styleSheet.checkbox)}
					onClick={e => e.stopPropagation()}
					aria-label={`Select ${fullName}`}
				/>
			</div>

			{showEmail && contact.primaryEmail?.value && (
				<div className={css(styleSheet.contactInfo)}>{contact.primaryEmail.value}</div>
			)}

			{showPhone && contact.phoneNumbers && contact.phoneNumbers.length > 0 && (
				<div className={css(styleSheet.contactInfo)}>{contact.phoneNumbers[0].value}</div>
			)}

			{contact.tags && contact.tags.length > 0 && (
				<div className={css(styleSheet.section)}>
					<div className={css(styleSheet.sectionLabel)}>Tags:</div>
					<div className={css(styleSheet.tags)}>
						{(showAllTags ? contact.tags : contact.tags.slice(0, 3)).map(tag => (
							<span key={tag} className={css(styleSheet.tag)}>
								{tag}
							</span>
						))}
						{!showAllTags && contact.tags.length > 3 && (
							<span className={css(styleSheet.moreIndicator)}>+{contact.tags.length - 3} more</span>
						)}
					</div>
				</div>
			)}

			{keyFactsList.length > 0 && (
				<div className={css(styleSheet.section)}>
					<div className={css(styleSheet.sectionLabel)}>Key Facts:</div>
					<div className={css(styleSheet.keyFacts)}>
						{(showAllKeyFacts ? keyFactsList : keyFactsList.slice(0, 2)).map((fact, index) => (
							<div key={index} className={css(styleSheet.keyFact)}>
								{fact}
							</div>
						))}
						{!showAllKeyFacts && keyFactsList.length > 2 && (
							<span className={css(styleSheet.moreIndicator)}>+{keyFactsList.length - 2} more</span>
						)}
					</div>
				</div>
			)}

			{renderAdditionalContent && renderAdditionalContent(contact)}
		</div>
	);
}

const styleSheet = StyleSheet.create({
	card: {
		padding: spacing.md,
		borderRadius: 8,
		border: '3px solid',
		backgroundColor: colors.background.light,
		transition: 'all 0.2s ease',
		cursor: 'pointer',
		position: 'relative',
		':hover': {
			boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
		},
		':focus': {
			outline: `3px solid ${colors.primary.main}`,
			outlineOffset: 2,
		},
	},
	disabled: {
		opacity: 0.5,
		cursor: 'not-allowed',
	},
	selected: {
		backgroundColor: colors.primary.light,
	},
	colorIndicator: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: 4,
		height: '100%',
		borderTopLeftRadius: 4,
		borderBottomLeftRadius: 4,
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: spacing.sm,
	},
	nameSection: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		flex: 1,
		minWidth: 0,
	},
	profilePic: {
		width: 32,
		height: 32,
		borderRadius: '50%',
		backgroundColor: THEME_COLORS.grey[200],
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		flexShrink: 0,
	},
	profileImage: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	},
	initials: {
		fontSize: 12,
		fontWeight: 600,
		color: THEME_COLORS.text.secondary,
	},
	name: {
		fontSize: 14,
		fontWeight: 600,
		color: THEME_COLORS.text.primary,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	checkbox: {
		width: 18,
		height: 18,
		cursor: 'pointer',
		flexShrink: 0,
	},
	contactInfo: {
		fontSize: 12,
		color: THEME_COLORS.text.secondary,
		marginBottom: spacing.xs,
	},
	section: {
		marginTop: spacing.sm,
	},
	sectionLabel: {
		fontSize: 11,
		fontWeight: 600,
		color: THEME_COLORS.text.secondary,
		marginBottom: spacing.xs,
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
	},
	tags: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: spacing.xs,
	},
	tag: {
		fontSize: 11,
		padding: `2px ${spacing.sm}px`,
		borderRadius: 4,
		backgroundColor: THEME_COLORS.grey[100],
		color: THEME_COLORS.text.primary,
		border: `1px solid ${THEME_COLORS.grey[300]}`,
	},
	keyFacts: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
	},
	keyFact: {
		fontSize: 12,
		color: THEME_COLORS.text.secondary,
	},
	moreIndicator: {
		fontSize: 11,
		color: THEME_COLORS.text.secondary,
		fontStyle: 'italic',
	},
});
